import type { JSX } from 'react';
import { useState } from 'react';
import { useForm, useWatch, Controller } from 'react-hook-form';
import { Select } from '@/components/shared/select';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import type { Provider, Model, Binding, Segment } from '@echo-work-platform/shared-types';
import { api, ApiError, conflictReason, deleteErrorMessage } from '@/lib/api-client';

interface ModelWithProvider extends Model {
  providerName: string;
  providerType: Provider['type'];
}

// Activation under an inactive provider is refused (EW-191): activation only works under an
// active provider, and activating the provider does not activate its models — the way back
// up is per model, by hand (docs/eroforras-eletciklus.md).
const INACTIVE_PROVIDER_MESSAGE =
  'A modell nem aktiválható, mert a providere inaktív.\n\n' +
  'Előbb aktiváld a providert a Providers oldalon — a provider aktiválása önmagában ' +
  'nem aktiválja a modelljeit, a modellt utána itt, külön kell aktiválni.';

// Attachment-input capability presets. 'inherit' = null in the API: the sidecar
// falls back to its models.dev catalog entry; on a catalog miss attachments are
// silently replaced with an error text, so explicit is the safe choice.
const MODALITY_CHOICES = {
  inherit: null,
  text: ['text'],
  'text-image': ['text', 'image'],
  'text-image-pdf': ['text', 'image', 'pdf'],
  'text-pdf': ['text', 'pdf'],
} as const;
type ModalityChoice = keyof typeof MODALITY_CHOICES;

function modalitiesToChoice(value: string[] | null | undefined): ModalityChoice {
  if (!value || value.length === 0) return 'inherit';
  const set = new Set(value);
  for (const [choice, list] of Object.entries(MODALITY_CHOICES)) {
    if (list && list.length === set.size && list.every((x) => set.has(x))) {
      return choice as ModalityChoice;
    }
  }
  return 'inherit';
}

function modalityLabel(value: string[] | null | undefined): string {
  const set = new Set(value ?? []);
  if (set.size === 0) return '—';
  const parts = [];
  if (set.has('image')) parts.push('kép');
  if (set.has('pdf')) parts.push('PDF');
  return parts.length > 0 ? parts.join('+') : 'szöveg';
}

interface FormValues {
  providerId: string;
  name: string;
  deploymentId: string;
  // EchoProxy-only: full deployment base URL + per-deployment API key.
  baseUrl: string;
  apiKeySecret: string;
  contextWindowTokens: number;
  maxOutputTokens: number;
  inputTokenPrice: number;
  cachedInputTokenPrice: number;
  cacheWriteTokenPrice: number;
  outputTokenPrice: number;
  reasoningTokenPrice: number;
  supportsReasoning: boolean;
  inputModalitiesChoice: ModalityChoice;
}

export function Models(): JSX.Element {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<ModelWithProvider | null>(null);

  const providers = useQuery({
    queryKey: ['/api/admin/providers'],
    queryFn: () => api.get<Provider[]>('/api/admin/providers'),
  });

  // All models across providers, flattened with provider context for display.
  const models = useQuery({
    queryKey: ['/api/admin/models-flat', providers.data?.length],
    enabled: !!providers.data,
    queryFn: async (): Promise<ModelWithProvider[]> => {
      const out: ModelWithProvider[] = [];
      for (const p of providers.data ?? []) {
        const ms = await api.get<Model[]>(`/api/admin/providers/${p.id}/models`);
        for (const m of ms) {
          out.push({ ...m, providerName: p.name, providerType: p.type });
        }
      }
      return out;
    },
  });

  const create = useMutation({
    mutationFn: async (v: FormValues) => {
      const { providerId, deploymentId, baseUrl, apiKeySecret, inputModalitiesChoice, ...payload } = v;
      return api.post<Model>(`/api/admin/providers/${providerId}/models`, {
        ...payload,
        inputModalities: MODALITY_CHOICES[inputModalitiesChoice],
        deploymentId: deploymentId?.trim() || null,
        baseUrl: baseUrl?.trim() || null,
        apiKeySecret: apiKeySecret?.trim() || null,
        contextWindowTokens: Number(payload.contextWindowTokens),
        maxOutputTokens: Number(payload.maxOutputTokens || 128000),
        inputTokenPrice: Number(payload.inputTokenPrice),
        cachedInputTokenPrice: Number(payload.cachedInputTokenPrice || 0),
        cacheWriteTokenPrice: Number(payload.cacheWriteTokenPrice || 0),
        outputTokenPrice: Number(payload.outputTokenPrice),
        reasoningTokenPrice: Number(payload.reasoningTokenPrice || 0),
        supportsReasoning: Boolean(payload.supportsReasoning),
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['/api/admin/models-flat'] });
      setShowForm(false);
    },
  });

  const update = useMutation({
    mutationFn: async ({ id, v }: { id: string; v: FormValues }) => {
      // providerId is fixed for an existing model (the API has no move-provider op),
      // so it is intentionally left out of the PATCH body.
      return api.patch<Model>(`/api/admin/models/${id}`, {
        name: v.name,
        deploymentId: v.deploymentId?.trim() || null,
        baseUrl: v.baseUrl?.trim() || null,
        // Only send the key when changed — the form shows a masked placeholder,
        // so leaving it untouched preserves the existing key (backend omits it).
        ...(v.apiKeySecret && v.apiKeySecret !== '••••••••' ? { apiKeySecret: v.apiKeySecret.trim() } : {}),
        contextWindowTokens: Number(v.contextWindowTokens),
        maxOutputTokens: Number(v.maxOutputTokens || 128000),
        inputTokenPrice: Number(v.inputTokenPrice),
        cachedInputTokenPrice: Number(v.cachedInputTokenPrice || 0),
        cacheWriteTokenPrice: Number(v.cacheWriteTokenPrice || 0),
        outputTokenPrice: Number(v.outputTokenPrice),
        reasoningTokenPrice: Number(v.reasoningTokenPrice || 0),
        supportsReasoning: Boolean(v.supportsReasoning),
        inputModalities: MODALITY_CHOICES[v.inputModalitiesChoice],
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['/api/admin/models-flat'] });
      setEditing(null);
    },
  });

  const setActive = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      api.patch<Model>(`/api/admin/models/${id}`, { isActive }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['/api/admin/models-flat'] }),
    onError: (err: unknown) => {
      // The confirmActivate pre-check is the friendlier path, this 409 is the guarantee
      // (a stale provider list, or a provider deactivated meanwhile).
      if (err instanceof ApiError && err.status === 409 && err.code === 'PROVIDER_INACTIVE') {
        alert(INACTIVE_PROVIDER_MESSAGE);
        return;
      }
      const reason = err instanceof ApiError ? conflictReason(err.body) : null;
      alert(`Nem sikerült átállítani a modellt.${reason ? `\n\n(${reason})` : ''}`);
    },
  });

  // Activation is immediate re-exposure through the bindings kept across deactivation
  // (EW-191, docs/eroforras-eletciklus.md): confirm with the segments that will receive
  // the model the moment it turns active.
  const confirmActivate = async (m: ModelWithProvider) => {
    // Validations run first: under an inactive provider the backend refuses the activation
    // anyway, so asking about segments before that would promise something that cannot happen.
    const provider = providers.data?.find((p) => p.id === m.providerId);
    if (provider && !provider.isActive) {
      alert(INACTIVE_PROVIDER_MESSAGE);
      return;
    }
    let message: string;
    try {
      const [modelBindings, segments] = await Promise.all([
        api.get<Binding[]>(`/api/admin/bindings?resourceType=MODEL&resourceId=${m.id}`),
        api.get<Segment[]>('/api/admin/segments'),
      ]);
      const names = modelBindings.map((b) => segments.find((s) => s.id === b.segmentId)?.name ?? b.segmentId);
      message =
        names.length > 0
          ? `Aktiválás után a(z) "${m.name}" modell azonnal elérhetővé válik ${names.length} szegmensben:\n${names.join(', ')}\n\nAktiválod?`
          : `A(z) "${m.name}" modell nincs bekötve egyetlen szegmensbe sem — aktiválás után is csak bekötés után jut el a felhasználókhoz.\n\nAktiválod?`;
    } catch {
      message = `Nem sikerült lekérdezni a(z) "${m.name}" modell kötéseit — az aktiválás a bekötött szegmensekben azonnal érvényes lesz.\n\nAktiválod?`;
    }
    if (confirm(message)) setActive.mutate({ id: m.id, isActive: true });
  };

  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/api/admin/models/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['/api/admin/models-flat'] }),
    onError: (err: unknown, id) => {
      // Backend blocks deleting a model that has recorded usage (would break cost reports).
      // Offer deactivation instead, which keeps the usage→model reference intact. Every other
      // failure goes through deleteErrorMessage — a model still bound to segments (EW-192)
      // must not be answered with deactivation: the binding has to go in its own audited step.
      if (err instanceof ApiError && err.status === 409 && err.body.includes('usageCount')) {
        if (
          confirm(
            'Ennek a modellnek már van rögzített használata, ezért nem törölhető (a költségriportok hivatkoznak rá).\n\nDeaktiváljam helyette? Az inaktív modell nem választható ki, de az előzmények megmaradnak.',
          )
        ) {
          setActive.mutate({ id, isActive: false });
        }
        return;
      }
      alert(deleteErrorMessage(err));
    },
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-slate-900">Models</h2>
        <button
          className="btn-primary"
          onClick={() => {
            setEditing(null);
            setShowForm((v) => !v);
          }}
          disabled={(providers.data?.length ?? 0) === 0}
        >
          <Plus className="h-4 w-4" /> {showForm ? 'Bezárás' : 'Új model'}
        </button>
      </div>

      {(providers.data?.length ?? 0) === 0 && (
        <p className="text-sm text-slate-500">Először vegyél fel egy Model Provider-t.</p>
      )}

      {showForm && !editing && providers.data && providers.data.length > 0 && (
        <ModelForm
          providers={providers.data}
          onCancel={() => setShowForm(false)}
          onSubmit={(v) => create.mutate(v)}
        />
      )}

      {editing && providers.data && (
        <ModelForm
          key={editing.id}
          providers={providers.data}
          initial={editing}
          onCancel={() => setEditing(null)}
          onSubmit={(v) => update.mutate({ id: editing.id, v })}
        />
      )}

      <div className="card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
            <tr>
              <th className="px-4 py-2">Provider</th>
              <th className="px-4 py-2">Név</th>
              <th className="px-4 py-2">Deployment</th>
              <th className="px-4 py-2">Context</th>
              <th className="px-4 py-2" title="Max output token (opencode limit.output)">
                Max out
              </th>
              <th className="px-4 py-2" title="Input $/1M token">
                Input
              </th>
              <th className="px-4 py-2" title="Cached input $/1M token">
                Cached
              </th>
              <th className="px-4 py-2" title="Cache write $/1M token">
                Cache w
              </th>
              <th className="px-4 py-2" title="Output $/1M token">
                Output
              </th>
              <th className="px-4 py-2" title="Reasoning $/1M token">
                Reason
              </th>
              <th className="px-4 py-2" title="Effort-szintek engedélyezve">
                Reasoning
              </th>
              <th className="px-4 py-2" title="Explicit fájl-bemenetek (— = katalógus szerint)">
                Fájlok
              </th>
              <th className="px-4 py-2">Állapot</th>
              <th className="w-12 px-4 py-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {models.isLoading && (
              <tr>
                <td colSpan={14} className="px-4 py-6 text-center text-slate-500">
                  Betöltés…
                </td>
              </tr>
            )}
            {models.data?.length === 0 && (
              <tr>
                <td colSpan={14} className="px-4 py-6 text-center text-slate-500">
                  Nincs model.
                </td>
              </tr>
            )}
            {models.data?.map((m) => (
              <tr key={m.id}>
                <td className="px-4 py-2 text-slate-700">{m.providerName}</td>
                <td className="px-4 py-2 font-medium">{m.name}</td>
                <td className="px-4 py-2 font-mono text-xs text-slate-500">
                  {m.providerType === 'ECHOPROXY' ? (m.baseUrl ?? '—') : (m.deploymentId ?? '—')}
                </td>
                <td className="px-4 py-2">{m.contextWindowTokens.toLocaleString()}</td>
                <td className="px-4 py-2">{(m.maxOutputTokens ?? 128000).toLocaleString()}</td>
                <td className="px-4 py-2">${m.inputTokenPrice}</td>
                <td className="px-4 py-2">${m.cachedInputTokenPrice ?? 0}</td>
                <td className="px-4 py-2">${m.cacheWriteTokenPrice ?? 0}</td>
                <td className="px-4 py-2">${m.outputTokenPrice}</td>
                <td className="px-4 py-2">${m.reasoningTokenPrice ?? 0}</td>
                <td className="px-4 py-2 text-center">
                  {m.supportsReasoning ? (
                    <span title="Effort-szintek engedélyezve" className="text-emerald-600">
                      ✓
                    </span>
                  ) : (
                    <span className="text-slate-300">—</span>
                  )}
                </td>
                <td className="px-4 py-2 text-center">
                  {m.inputModalities && m.inputModalities.length > 0 ? (
                    <span title="Explicit fájl-bemenetek" className="text-slate-700">
                      {modalityLabel(m.inputModalities)}
                    </span>
                  ) : (
                    <span title="Katalógus szerint" className="text-slate-300">
                      —
                    </span>
                  )}
                </td>
                <td className="px-4 py-2">
                  <button
                    title={m.isActive ? 'Kattints a deaktiváláshoz' : 'Kattints az aktiváláshoz'}
                    onClick={() => {
                      if (m.isActive) setActive.mutate({ id: m.id, isActive: false });
                      else void confirmActivate(m);
                    }}
                    className={
                      m.isActive
                        ? 'rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700 hover:bg-emerald-100'
                        : 'rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-500 hover:bg-slate-200'
                    }
                  >
                    {m.isActive ? 'Aktív' : 'Inaktív'}
                  </button>
                </td>
                <td className="px-4 py-2">
                  <div className="flex items-center gap-1">
                    <button
                      title="Szerkeszt"
                      className="rounded p-1 text-slate-500 hover:bg-slate-100"
                      onClick={() => {
                        setShowForm(false);
                        setEditing(m);
                      }}
                    >
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button
                      title="Töröl"
                      className="rounded p-1 text-rose-500 hover:bg-rose-50"
                      onClick={() => {
                        if (confirm(`Törlés: "${m.name}"?`)) remove.mutate(m.id);
                      }}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="px-4 py-2 text-xs text-slate-500">Minden ár USD / 1M token.</p>
      </div>
    </div>
  );
}

function ModelForm({
  providers,
  initial,
  onSubmit,
  onCancel,
}: {
  providers: Provider[];
  initial?: ModelWithProvider | null;
  onSubmit: (v: FormValues) => void;
  onCancel: () => void;
}): JSX.Element {
  const isEdit = Boolean(initial);
  const { register, handleSubmit, control } = useForm<FormValues>({
    defaultValues: initial
      ? {
          providerId: initial.providerId,
          name: initial.name,
          deploymentId: initial.deploymentId ?? '',
          baseUrl: initial.baseUrl ?? '',
          apiKeySecret: initial.apiKeySecret ? '••••••••' : '',
          contextWindowTokens: initial.contextWindowTokens,
          maxOutputTokens: initial.maxOutputTokens,
          inputTokenPrice: initial.inputTokenPrice,
          cachedInputTokenPrice: initial.cachedInputTokenPrice,
          cacheWriteTokenPrice: initial.cacheWriteTokenPrice,
          outputTokenPrice: initial.outputTokenPrice,
          reasoningTokenPrice: initial.reasoningTokenPrice,
          supportsReasoning: initial.supportsReasoning,
          inputModalitiesChoice: modalitiesToChoice(initial.inputModalities),
        }
      : {
          providerId: providers[0]?.id ?? '',
          maxOutputTokens: 128000,
          supportsReasoning: false,
          inputModalitiesChoice: 'inherit',
        },
  });
  const watchedProviderId = useWatch({ control, name: 'providerId' });
  const selectedProvider = providers.find((p) => p.id === watchedProviderId);
  const isAzure = selectedProvider?.type === 'AZURE_OPENAI';
  const isBedrock = selectedProvider?.type === 'BEDROCK';
  const isEchoProxy = selectedProvider?.type === 'ECHOPROXY';

  return (
    <form className="card space-y-3 p-4" onSubmit={(e) => void handleSubmit(onSubmit)(e)}>
      <div className="text-sm font-semibold text-slate-700">
        {isEdit ? `Model szerkesztése: ${initial?.name}` : 'Új model'}
      </div>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
        <div>
          <label className="label">Provider</label>
          {isEdit ? (
            // Provider is fixed for an existing model (no move-provider API).
            <input
              className="input bg-slate-50 text-slate-500"
              value={initial?.providerName ?? ''}
              disabled
              readOnly
            />
          ) : (
            <Controller
              control={control}
              name="providerId"
              rules={{ required: true }}
              render={({ field }) => (
                <Select
                  value={field.value ?? ''}
                  onChange={field.onChange}
                  placeholder="— válassz providert —"
                  options={providers.map((p) => ({ value: p.id, label: p.name }))}
                />
              )}
            />
          )}
        </div>
        <div>
          <label className="label">Model név</label>
          <input className="input" placeholder="pl. gpt-5.4" {...register('name', { required: true })} />
        </div>
        <div>
          <label className="label">Context window (token)</label>
          <input
            className="input"
            type="number"
            placeholder="128000"
            {...register('contextWindowTokens', { required: true })}
          />
        </div>
        <div>
          <label className="label">Max output token</label>
          <input
            className="input"
            type="number"
            placeholder="128000"
            {...register('maxOutputTokens', { required: true })}
          />
          <p className="mt-1 text-xs text-slate-500">
            A reasoning-modellek opencode <code>limit.output</code> értéke. Default 128000.
          </p>
        </div>
      </div>
      {(isAzure || isBedrock) && (
        <div>
          <label className="label">
            {isAzure ? 'Deployment name' : 'Inference profile ID'} <span className="text-rose-500">*</span>
          </label>
          <input
            className="input"
            placeholder={
              isAzure
                ? 'pl. gpt-5.4 (az Azure-portálon megadott deployment-név)'
                : 'pl. eu.anthropic.claude-opus-4-7 vagy global.anthropic.claude-opus-4-7'
            }
            {...register('deploymentId', { required: isAzure || isBedrock })}
          />
          <p className="mt-1 text-xs text-slate-500">
            {isAzure
              ? 'Az Azure-portálon a deployment-nek adott egyedi név. Az API-hívás ezen át fog menni.'
              : 'AWS Bedrock inference profile ID. EU példa: eu.anthropic.claude-opus-4-7 (a provider AWS Region eu-* legyen).'}
          </p>
        </div>
      )}
      {isEchoProxy && (
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          <div>
            <label className="label">
              Deployment URL <span className="text-rose-500">*</span>
            </label>
            <input
              className="input"
              placeholder="https://echoproxy.intuitech.studio/d/<slug>/v1"
              {...register('baseUrl', { required: isEchoProxy })}
            />
            <p className="mt-1 text-xs text-slate-500">
              Az EchoProxy deployment teljes base URL-je (az OpenAI SDK <code>baseURL</code>-je). A „Model
              név" a deployment által visszaadott model-id.
            </p>
          </div>
          <div>
            <label className="label">
              API Key <span className="text-rose-500">*</span>
            </label>
            <input
              className="input"
              type="password"
              placeholder="ep_dep_…"
              {...register('apiKeySecret', { required: isEchoProxy && !isEdit })}
            />
            {isEdit && (
              <p className="mt-1 text-xs text-slate-500">Hagyd üresen a meglévő kulcs megtartásához.</p>
            )}
          </div>
        </div>
      )}
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
        <div>
          <label className="label text-xs">Input $/1M</label>
          <input
            className="input"
            type="number"
            step="0.01"
            placeholder="5.00"
            {...register('inputTokenPrice', { required: true })}
          />
        </div>
        <div>
          <label className="label text-xs">Cached $/1M</label>
          <input
            className="input"
            type="number"
            step="0.01"
            placeholder="0.50"
            {...register('cachedInputTokenPrice')}
          />
        </div>
        <div>
          <label className="label text-xs">Cache write $/1M</label>
          <input
            className="input"
            type="number"
            step="0.01"
            placeholder="0.00"
            {...register('cacheWriteTokenPrice')}
          />
        </div>
        <div>
          <label className="label text-xs">Output $/1M</label>
          <input
            className="input"
            type="number"
            step="0.01"
            placeholder="15.00"
            {...register('outputTokenPrice', { required: true })}
          />
        </div>
        <div>
          <label className="label text-xs">Reasoning $/1M</label>
          <input
            className="input"
            type="number"
            step="0.01"
            placeholder="0.00"
            {...register('reasoningTokenPrice')}
          />
        </div>
      </div>
      <label className="flex items-center gap-2 text-sm text-slate-700">
        <input type="checkbox" className="h-4 w-4" {...register('supportsReasoning')} />
        Reasoning (effort-szintek engedélyezése)
      </label>
      <p className="-mt-1 text-xs text-slate-500">
        Ha be van kapcsolva, a kliens modellválasztójában megjelenik az effort-szint (low/medium/high) az
        adott modellhez. Reasoning-képes modelleknél kapcsold be (pl. GPT-5, o-széria, Claude extended
        thinking).
      </p>
      <div>
        <label className="label text-xs">Fájl-bemenetek (csatolmányok)</label>
        <select className="input" {...register('inputModalitiesChoice')}>
          <option value="inherit">Katalógus szerint (alap)</option>
          <option value="text">Csak szöveg</option>
          <option value="text-image">Szöveg + kép</option>
          <option value="text-image-pdf">Szöveg + kép + PDF</option>
          <option value="text-pdf">Szöveg + PDF</option>
        </select>
        <p className="mt-1 text-xs text-slate-500">
          Explicit beállítással a kép/PDF csatolmányok támogatása nem a modellkatalógus találatán múlik —
          katalógus-találat híján a kliens a csatolmányt némán hibaszövegre cseréli. Ha a modell tud képet
          vagy PDF-et fogadni, állítsd be itt.
        </p>
      </div>
      <div className="flex justify-end gap-2">
        <button type="button" className="btn-secondary" onClick={onCancel}>
          Mégse
        </button>
        <button type="submit" className="btn-primary">
          <Plus className="h-4 w-4" /> Mentés
        </button>
      </div>
    </form>
  );
}
